import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listPerformanceReview,
  fetchPerformanceReview,
  createPerformanceReview,
  deletePerformanceReview,
} from '@/apis/performanceReviews';
import { usePerformanceReviewState } from '@/store/usePerformanceReviewState';
import React from 'react';
import { GitProvider } from '@/types/providerUser';
import { useSearchParams } from 'next/navigation';
import { SEATS_ONLY_PARAM, resolveSeatsOnly } from '@/hooks/useLeaderboard';

export const usePerformanceReviews = (
  orgUUID: string,
  teamUUID?: string,
  startDate?: string,
  endDate?: string
) => {
  const { setReviews } = usePerformanceReviewState();
  // Same `?seats=paid` param and predicate as the dashboard hook beside it.
  // Individual Insights renders both, and PerformanceDistributionChart prefers
  // THIS list — so if only the dashboard were scoped, the chart would still
  // show non-seat engineers under a "Paid seats only" toggle.
  const searchParams = useSearchParams();
  const seatsOnly = resolveSeatsOnly({
    orgUUID,
    seatsParam: searchParams?.get(SEATS_ONLY_PARAM),
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'performanceReviews',
      orgUUID,
      teamUUID,
      startDate,
      endDate,
      // Part of the key: filtered and unfiltered lists are different data and
      // must not share a cache entry.
      seatsOnly,
    ],
    queryFn: async () => {
      if (!orgUUID) {
        return { reviews: [] }; // Return consistent shape
      }

      try {
        const response = await listPerformanceReview({
          OrgUUID: orgUUID,
          TeamUUID: teamUUID,
          StartDate: startDate,
          EndDate: endDate,
          seatsOnly,
        });
        return response || { reviews: [] };
      } catch (err) {
        console.error('Error fetching performance reviews:', err);
        return { reviews: [] };
      }
    },
    enabled: !!orgUUID, // Only run query if orgUUID exists
  });

  // Update state when data changes
  React.useEffect(() => {
    if (data) {
      setReviews(data?.reviews ?? []);
    }
  }, [data, setReviews]);

  return { reviews: data?.reviews ?? [], isLoading, error, refetch };
};

export const useFetchReview = (uuid: string) => {
  const setCurrentReview = usePerformanceReviewState(
    (state) => state.setCurrentReview
  );

  return useQuery({
    queryKey: ['performanceReview', uuid],
    queryFn: async () => {
      const response = await fetchPerformanceReview({ UUID: uuid });
      setCurrentReview(response.data);
      return response.data;
    },
    enabled: !!uuid,
  });
};

export const useGeneratePerformanceReview = (orgUUID: string) => {
  const { refetch: refetchPerformanceReviews } = usePerformanceReviews(orgUUID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      UserUUID,
      TeamUUID,
      OrgUUID,
      TeamName,
      Months,
      Platform,
    }: {
      UserUUID: string;
      TeamUUID: string;
      OrgUUID: string;
      TeamName: string;
      Months?: number;
      Platform?: GitProvider;
    }) => {
      const response = await createPerformanceReview({
        UserUUID,
        OrgUUID,
        TeamUUID,
        TeamName,
        Months,
        Platform,
      });

      // Parse the response if it's a string
      const parsedResponse =
        typeof response === 'string' ? JSON.parse(response) : response;

      // Check if there's an error in the response
      if (parsedResponse.Error) {
        throw new Error(parsedResponse.Error);
      }

      return parsedResponse;
    },
    onSuccess: async (_, variables) => {
      // Refetch the reviews list
      await refetchPerformanceReviews();

      // Invalidate all performance review queries to force fresh data
      queryClient.invalidateQueries({
        queryKey: ['performanceReview'],
      });
    },
  });
};

const useDeletePerformanceReview = (orgUUID: string) => {
  const { refetch: refetchPerformanceReviews } = usePerformanceReviews(orgUUID);
  return useMutation({
    mutationFn: async ({ UUID }: { UUID: string }) => {
      const response = await deletePerformanceReview({
        UUID,
      });
      return response;
    },
    onSuccess: () => {
      refetchPerformanceReviews();
    },
  });
};
